import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import SuperadminGate from "@/components/SuperadminGate";

const LOGO = "/api/branding/icon/elite-logo-dark.png";
const LOGO_LIGHT = "/api/branding/icon/elite-logo-light.png";

const ease = [0.76, 0, 0.24, 1];

const Login = ({ appName }) => {
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user: cachedUser } = useAuth();

  // If we already have a cached user from a previous session, jump straight
  // to the dashboard — no need to reauthenticate on every cold start.
  useEffect(() => {
    if (cachedUser) navigate(`/dashboard${window.location.search}`, { replace: true });
  }, [cachedUser, navigate]);

  const handleGoogleLogin = () => {
    setLoading(true);
    // Persist the "stay logged in" preference so AuthCallback can use it.
    localStorage.setItem("em_remember", remember ? "1" : "0");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl
    )}`;
  };

  return (
    <div id="login-page" data-brand={appName || "Elite Mechanical"} className="relative z-10 min-h-screen w-full">
      <SuperadminGate />
      {/* top bar */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease, delay: 0.2 }}
        id="login-header"
        className="flex items-center justify-between px-6 pb-6 pt-[max(1.5rem,env(safe-area-inset-top))] sm:px-10 lg:px-16"
        data-testid="portal-header"
      >
        <div className="flex items-center gap-3">
          <span id="login-dot" className="h-2 w-2 rounded-full bg-[#4f6fe8] shadow-[0_0_12px_#1a3fd1]" />
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-white/70" data-testid="login-brand-name">
            {appName || "Elite Mechanical"}
          </span>
        </div>
        <span className="hidden font-mono text-xs uppercase tracking-[0.25em] text-white/40 sm:block">
          Secure&nbsp;Portal&nbsp;/&nbsp;v1.0
        </span>
      </motion.header>

      {/* main — centered frosted glass login card */}
      <div id="login-main" className="mx-auto flex min-h-[calc(100vh-88px)] max-w-md items-center px-6 py-6 sm:px-10">
        {/* frosted glass login card */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1, ease, delay: 0.3 }}
          className="w-full"
        >
          <div
            id="login-card"
            data-testid="login-card"
            className="relative overflow-hidden rounded-2xl border border-white/15 bg-white/[0.02] p-6 shadow-[0_0_60px_rgba(26,63,209,0.15)] backdrop-blur-[3px] sm:p-8"
          >
            {/* corner accent */}
            <div id="login-glow" className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-[#1a3fd1]/30 blur-3xl" />

            <div className="relative">
              <div id="login-logo-wrap" className="mb-6 flex items-center justify-center">
                <img
                  src={LOGO}
                  alt="Elite Mechanical"
                  className="logo-dark-only w-full max-w-full object-contain drop-shadow-[0_0_22px_rgba(26,63,209,0.4)]"
                  data-testid="login-logo"
                />
                <img
                  src={LOGO_LIGHT}
                  alt="Elite Mechanical"
                  className="logo-light-only w-full max-w-full object-contain"
                  data-testid="login-logo-light"
                />
              </div>

              {/* Google sign-in */}
              <motion.button
                whileHover={{ scale: 1.015 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleGoogleLogin}
                disabled={loading}
                id="login-google-btn"
                data-testid="google-login-btn"
                className="group relative flex w-full items-center justify-center gap-3 rounded-xl bg-white px-6 py-4 font-semibold text-[#0b0b0b] shadow-lg transition-[box-shadow,background-color] duration-300 hover:bg-white/95 hover:shadow-[0_0_30px_rgba(255,255,255,0.25)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1a3fd1] focus-visible:ring-offset-2 focus-visible:ring-offset-black disabled:cursor-not-allowed disabled:opacity-70"
              >
                <GoogleIcon />
                <span className="text-sm">
                  {loading ? "Redirecting…" : "Continue with Google"}
                </span>
                <ArrowUpRight className="h-4 w-4 opacity-40 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </motion.button>

              {/* stay logged in */}
              <label
                id="login-remember"
                htmlFor="stay-logged-in"
                className="mt-6 flex cursor-pointer items-center gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 transition-colors duration-300 hover:border-white/20"
              >
                <Checkbox
                  id="stay-logged-in"
                  checked={remember}
                  onCheckedChange={(v) => setRemember(Boolean(v))}
                  data-testid="stay-logged-in-checkbox"
                  className="border-white/30 data-[state=checked]:border-[#1a3fd1] data-[state=checked]:bg-[#1a3fd1]"
                />
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-white/90">
                    Stay logged in
                  </span>
                  <span className="text-xs text-white/40">
                    Keep me signed in on this device
                  </span>
                </div>
              </label>
            </div>
          </div>
        </motion.div>
      </div>

    </div>
  );
};

const GoogleIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </svg>
);

export default Login;
